import type {
  PlanInfoBenefitCategory,
  PlanInfoBenefitService,
} from "../@types";
import {
  planInfoBenefitCategories,
  planInfoBenefitServices,
} from "./constants/benefits";
import { getPrefixedString } from "./stringUtilities";

/**
 * For normalizing benefit category names
 * @see https://imp.medicare.gov/api/v1/data/plan-compare/swagger/mctapi.swagger.json
 * search `planinfoBenefitCategory`
 * If the passed in string, uppercased, exactly matches an existing benefit category
 * name from MCTAPI, it will be returned as-is, uppercased
 */
export const getBenefitCategoryName = (
  benefitString: string | PlanInfoBenefitCategory
) => {
  const ucBenefitString = benefitString.toUpperCase();
  const isExistingBenefit = (planInfoBenefitCategories as string[]).includes(
    ucBenefitString
  );
  return isExistingBenefit
    ? (ucBenefitString as PlanInfoBenefitCategory)
    : (getPrefixedString({
        initialString: benefitString,
        prefix: "benefit",
        ignoreRegex: /^SB_/i, // ignore supplemental benefit names
      }).toUpperCase() as PlanInfoBenefitCategory);
};

/**
 * For normalizing benefit service names
 * @see https://imp.medicare.gov/api/v1/data/plan-compare/swagger/mctapi.swagger.json
 * search `planinfoBenefitService`
 * If the passed in string, uppercased, exactly matches an existing benefit service
 * name from MCTAPI, it will be returned as-is, uppercased
 */
export const getBenefitServiceName = (
  serviceString: string | PlanInfoBenefitService
) => {
  const ucServiceString = serviceString.toUpperCase();
  const isExistingService = (planInfoBenefitServices as string[]).includes(
    ucServiceString
  );
  const returnValue = isExistingService
    ? (ucServiceString as PlanInfoBenefitService)
    : getPrefixedString({
        initialString: serviceString,
        prefix: "service",
      }).toUpperCase();
  return returnValue as PlanInfoBenefitService;
};

export const getSupplementalBenefitName = (serviceString: string) =>
  getPrefixedString({
    initialString: serviceString,
    prefix: "sb",
  }).toUpperCase();

export const getSupplementalBenefitCategoryName = (benefitString: string) =>
  getPrefixedString({
    initialString: benefitString,
    prefix: "sb_cat",
  }).toUpperCase();
